/**
 * Головна: розділ «Штучний інтелект для допомоги» (рішення власника
 * 2026-10-02, замість блоку «Ласкаво просимо») — три картки сторінок, де
 * працює агент Oblik+/AI. Доступність картки — та сама, що й пункту меню
 * (модуль + право): недоступна картка приглушена і без посилання; якщо
 * користувачу недоступна жодна — розділ не показується.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { usePermissions } from '@/auth/usePermissions';
import { hasModule, useEnabledModules } from '@/hooks/useEnabledModules';
import { SIDEBAR_ITEMS, type SidebarLink } from '@/layouts/sidebarItems';
import AiBadge from '@/shared/components/AiBadge';

interface AiCard {
  to: string;
  title: string;
  text: string;
  more: string;
  icon: ReactNode;
}

const svg = (children: ReactNode) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);

export const AI_CARDS: AiCard[] = [
  {
    to: '/audit/postings',
    title: 'Аудит проводок',
    text: 'Переглядає проводки за період і показує місця, схожі на помилки: розрахунки без '
      + 'контрагента, дублі документів, відʼємні залишки грошей. Пояснює знахідку і підказує, '
      + 'що виправити.',
    more: 'перевірити проводки →',
    icon: svg(<><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /><path d="m8.5 11 1.8 1.8 3.4-3.6" /></>),
  },
  {
    to: '/incoming-documents',
    title: 'Оператор первинки',
    text: 'Читає рахунки, накладні й акти постачальників з PDF, фото, Excel чи Word, зіставляє '
      + 'рядки з вашою номенклатурою і готує чернетку документа — без ручного введення.',
    more: 'завантажити документи →',
    icon: svg(<><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5" /><path d="M9 13h6M9 17h4" /></>),
  },
  {
    to: '/bank-import',
    title: 'Рознесення виписки',
    text: 'Розбирає банківську виписку: для рядків, які не розпізнались автоматично, пропонує '
      + 'контрагента, вид операції і статтю руху коштів — вам лишається підтвердити.',
    more: 'розібрати виписку →',
    icon: svg(<><path d="M3 10 12 4l9 6" /><path d="M5 10v8M9.5 10v8M14.5 10v8M19 10v8" /><path d="M3 20h18" /></>),
  },
];

export default function AiHelpers() {
  const permissions = usePermissions();
  const modules = useEnabledModules();
  const allowed = (to: string): boolean => {
    const link = SIDEBAR_ITEMS.find((it): it is SidebarLink => it.type === 'link' && it.to === to);
    if (!link) return false;
    if (link.module !== null && !hasModule(modules, link.module)) return false;
    if (permissions.isLoading || link.permission === null) return true;
    return permissions.has(link.permission);
  };
  const cards = AI_CARDS.map((c) => ({ ...c, ok: allowed(c.to) }));
  if (!cards.some((c) => c.ok)) return null;

  return (
    <div className="card" data-testid="ai-helpers">
      <h2 style={{ marginTop: 0, marginBottom: '0.25rem' }}>Штучний інтелект для допомоги</h2>
      <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
        Помічники Oblik+/AI беруть на себе рутину бухгалтера. Вони лише готують і підказують —
        рішення завжди за вами.
      </p>
      <div className="ai-cards">
        {cards.map((c) => {
          const body = (
            <>
              <span className="ai-card__icon">{c.icon}</span>
              <span className="ai-card__title">{c.title}<AiBadge /></span>
              <span className="ai-card__text">{c.text}</span>
              <span className="ai-card__more">
                {c.ok ? c.more : 'недоступно для вашої ролі — зверніться до адміністратора акаунта'}
              </span>
            </>
          );
          return c.ok
            ? <Link key={c.to} to={c.to} className="ai-card">{body}</Link>
            : <div key={c.to} className="ai-card is-locked" aria-disabled="true">{body}</div>;
        })}
      </div>
    </div>
  );
}
